import {
  Box,
  Card,
  CardContent,
  Drawer,
  Grid,
  MenuItem,
  SelectChangeEvent,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Container,
  AccessRuleForm,
  MaiFooterSubmit,
  Main,
  MainContent,
  MainFooter,
  MainTitle,
  MainTitleLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "./style";
import { Close, InfoOutlined } from "@mui/icons-material";
import Button from "@mui/material/Button";
import { GroupType, ResourceName, ResourceSubGroup, RoleType, Status } from "Util/enums";
import Input from "Components/Form/Input";
import * as yup from "yup";
import Select from "Components/Form/Select";
import { useAppState } from "Context/StateContext";
import useStyles from "./style";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "reactstrap";
import { AxiosError } from "axios";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { Roles as IRoles } from "../../../../../../../../models/user";
import useActions from "../../useActions";
import useGroupActions from "../../../Groups/useAction";
import ResourceService from "Services/resource.service";
import {
  ResourceFeature,
  ResourceFunction,
  Resources,
  RolePermissionsResponse,
  RoleResource,
} from "Models/resources";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  IconTypo,
  InfoDetails,
} from "../../../../../../../../modules/moving/components/Diary/views/DiaryResourcesView/subcomponent/Viewers/ResourceTaskViewer/TaskCard/styles";
import Checkbox from "Components/Form/Checkbox";
import SkeletonComponent from "Components/Generic/Skeleton";
import ShipeeziNewGrid, { Column } from "Components/AppGrid/shipeezi-new-grid";
import { Group } from "Models/group";
import RoleService from "Services/role.service";
import GroupService from "Services/group.service";
import {
  AccessRuleEnum,
  ResourceGroupNameEnum,
  ResourceObjectTypeEnum,
} from "Util/enums/accessRule";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses, TooltipProps } from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import {
  POSITION_MENU_MV,
  RESOURCE_HAVE_VIEW_FEATURE,
  RESOURCE_HAVE_MASTER_DATA,
  POSITION_MENU_SP,
} from "Constants";
import { isMoving, isSupplyChain } from "Util/validation";
import { AccessRuleFeatureFunctionCard } from "./AccessRuleFeatureFunctionCard";
import { useAccessRuleDrawerStates } from "./useAccessRuleDrawerStates";

const activityIcon = require("Assets/icons/activity.svg");
const saleIcon = require("Assets/icons/icon.svg");
const operationIcon = require("Assets/icons/operation.svg");
const warehouseIcon = require("Assets/icons/warehouse.svg");
const reportIcon = require("Assets/icons/report.svg");
const networkIcon = require("Assets/icons/network.svg");
const settingIcon = require("Assets/icons/setting.svg");
const arrowRightIcon = require("Assets/icons/arrow-right-2.svg");
const duplicateIcon = require("Assets/icons/duplicate.svg");
const invoicesIcon = require("Assets/icons/document-text.svg");
const regexSpecialCharacter = /^[a-zA-Z0-9 ]*$/;
const SETTING_RESOURCE_NAMES_CONFIG = [
  "Company",
  "People",
  "Resources",
  "Configurations",
  "Templates",
];

type ResourceGroups = {
  [x: string]: Resources[];
};

export interface AccessRuleDrawerHandles {
  open: (accessRule: IRoles) => void;
}

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#475467",
    maxWidth: "500px",
    height: "24px",
    gap: "10px",
    borderRadius: "4px",
    padding: "4px 8px",
    fontFamily: "Manrope",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "16px",
    letterSpacing: "0.4px",
    verticalAlign: "middle",
    color: "#FFFFFF",
    margin: "0px",
  },
}));

export const AccessRuleDrawer = React.forwardRef<AccessRuleDrawerHandles, any>((_props, ref) => {
  const {
    createRole,
    updateAccessRule,
    deleteAccessRule,
    duplicateRole,
    errorSpecialCharacter,
    setErrorSpecialCharacter,
    errorUniqueAccessRuleName,
    setErrorUniqueAccessRuleName,
    isCreating,
    isDuplicating,
    isLoadingUpdatingAccessRule,
    isDeletingAccessRule,
    setPermissions,
    isMutatingAccessRule,
  } = useActions();
  const { statusConverter } = useGroupActions();
  const [accessRuleForm, setAccessRuleForm] = useState<{
    name: string;
    description: string;
    status: string;
  }>({
    name: "",
    description: "",
    status: Status.ACTIVE,
  });
  const classes = useStyles();
  const { currentUser } = useAppState();
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [isOpenAccessRuleInfoViewOnly, setIsOpenAccessRuleInfoViewOnly] = useState<boolean>(false);
  const [isDisabledSubmit, setIsDisabledSubmit] = useState<boolean>(true);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);
  const [isDisabledForm, setIsDisabledForm] = useState<boolean>(false);
  const [isShowLoading, setIsShowLoading] = useState<boolean>(false);
  const [resourceGroupDictionary, setResourceGroupsDictionary] = useState<ResourceGroups>({});
  const [settingResource, setSettingResource] = useState<Resources>({} as Resources);
  const [memberResource, setMemberResource] = useState<Resources>({} as Resources);
  const [subContractorResource, setSubContractorResource] = useState<Resources>({} as Resources);
  const [equipmentResource, setEquipmentResource] = useState<Resources>({} as Resources);
  const [networkResource, setNetworkResource] = useState<Resources>({} as Resources);
  const [reportResource, setReportResource] = useState<Resources>({} as Resources);
  const [resourcesExpand, setResourcesExpand] = useState<{ name: string; expanded: boolean }[]>([]);
  const [viewGroupsAssignedToAccessRule, setViewGroupsAssignedToAccessRule] =
    useState<boolean>(false);
  const [groupsInAccessRule, setGroupsInAccessRule] = useState<Group[]>([]);

  const [page, setPage] = useState<number>(0);
  const {
    accessRule,
    functionPermissions,
    isOpenAccessRuleDrawer,
    masterDataPermissions,
    roleResources,
    setAccessRule,
    setFunctionPermissions,
    setIsOpenAccessRuleDrawer,
    setMasterDataPermissions,
    setRoleResources,
  } = useAccessRuleDrawerStates();
  const { t: translate } = useTranslation();
  const accessRuleStatus: { value: string; name: string }[] = useMemo(
    () => [
      { value: Status.ACTIVE, name: Status.ACTIVE },
      { value: Status.INACTIVE, name: Status.INACTIVE },
    ],
    []
  );
  const resourceIcons: Record<string, string> = {
    Dashboard: activityIcon,
    Overview: activityIcon,
    Sales: saleIcon,
    Operations: operationIcon,
    Warehousing: warehouseIcon,
    Reports: reportIcon,
    Analytics: reportIcon,
    Network: networkIcon,
    Networks: networkIcon,
    Settings: settingIcon,
    Invoices: invoicesIcon,
  };

  const getResourceGroupName = (groupName: string) => {
    if (groupName === ResourceGroupNameEnum.OVERVIEW) {
      return ResourceGroupNameEnum.DASHBOARD;
    }

    if (groupName === ResourceGroupNameEnum.ANALYTICS) {
      return ResourceGroupNameEnum.REPORTS;
    }

    if (groupName === AccessRuleEnum.PEOPLE) {
      return ResourceGroupNameEnum.MEMBERS;
    }

    return groupName;
  };

  const setCurrentPage = (newPage: number) => {
    setPage(newPage > 0 ? newPage - 1 : newPage);
  };

  const handleOrderResources = (resourceGroups: ResourceGroups) => {
    const positionMenu: any = isMoving(currentUser?.company) ? POSITION_MENU_MV : POSITION_MENU_SP;
    const orderedKeys = Object.keys(positionMenu);
    const orderedResourceGroups = orderedKeys.reduce((acc: any, key: any) => {
      if (resourceGroups[key]) {
        if (Object.keys(positionMenu[key]).length > 0) {
          const subItems = positionMenu[key];
          const orderedSubKeys = Object.keys(subItems);
          const reorderedSubItems = orderedSubKeys.reduce((subAcc: any, subKey: any) => {
            const subResource = resourceGroups[key]?.find((resource) => resource?.name === subKey);
            if (subResource) {
              subAcc = [...subAcc, subResource];
            }
            return subAcc;
          }, []);

          acc[key] = reorderedSubItems;
        } else {
          acc[key] = resourceGroups[key];
        }
      }
      return acc;
    }, {});

    return orderedResourceGroups;
  };

  const { isFetching } = useQuery(
    ["permissions.hierarchy"],
    () => ResourceService.getResourceHierarchy(),
    {
      enabled: isOpenAccessRuleDrawer && !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          let resourceGroups: ResourceGroups = {};
          response.data?.resources?.forEach((resource) => {
            if (
              resource?.name === ResourceGroupNameEnum.NETWORK &&
              resource?.subGroup === ResourceGroupNameEnum.NETWORK &&
              resource?.group === ResourceGroupNameEnum.NETWORK
            ) {
              setNetworkResource(resource);
            }

            if (
              resource?.name === ResourceGroupNameEnum.ANALYTICS &&
              resource?.subGroup === ResourceGroupNameEnum.ANALYTICS &&
              resource?.group === ResourceGroupNameEnum.ANALYTICS
            ) {
              setReportResource(resource);
            }

            if (
              resource?.name === ResourceGroupNameEnum.SETTINGS &&
              resource?.subGroup === ResourceGroupNameEnum.SETTINGS &&
              resource?.group === ResourceGroupNameEnum.SETTINGS
            ) {
              setSettingResource(resource);
            }

            if (
              resource?.name === ResourceName.MEMBERS &&
              resource?.subGroup === AccessRuleEnum.PEOPLE &&
              resource?.group === ResourceGroupNameEnum.SETTINGS
            ) {
              setMemberResource(resource);
            }

            if (
              resource?.name === ResourceName.SUBCONTRACTORS &&
              resource?.subGroup === AccessRuleEnum.RESOURCES &&
              resource?.group === ResourceGroupNameEnum.SETTINGS
            ) {
              setSubContractorResource(resource);
            }

            if (
              resource?.name === ResourceName.EQUIPMENTS &&
              resource?.subGroup === AccessRuleEnum.RESOURCES &&
              resource?.group === ResourceGroupNameEnum.SETTINGS
            ) {
              setEquipmentResource(resource);
            }

            if (
              getResourceGroupName(resource?.group) !== AccessRuleEnum.VIDEOCALL &&
              getResourceGroupName(resource?.group) !== AccessRuleEnum.MANAGEMENT
            ) {
              const keys = Object.keys(resourceGroups);

              if (keys.length === 0 || !keys.includes(getResourceGroupName(resource.group)))
                resourceGroups[getResourceGroupName(resource.group)] = [resource];
              else if (keys.includes(getResourceGroupName(resource.group)))
                resourceGroups[getResourceGroupName(resource.group)].push(resource);
            }
          });
          resourceGroups[ResourceGroupNameEnum.SETTINGS] = resourceGroups[
            ResourceGroupNameEnum.SETTINGS
          ]?.filter((resource) => SETTING_RESOURCE_NAMES_CONFIG.includes(resource?.subGroup));

          let resourceSettings: any = {};
          resourceGroups[ResourceGroupNameEnum.SETTINGS]?.forEach((resource) => {
            if (
              SETTING_RESOURCE_NAMES_CONFIG.includes(resource?.subGroup) &&
              resource?.features &&
              resource?.features?.length > 0
            ) {
              const keys = Object.keys(resourceSettings);
              const resourceSettingSubGroup = resourceGroups[ResourceGroupNameEnum.SETTINGS]?.find(
                (resourceTemp) => {
                  return (
                    resourceTemp.name === resource.subGroup && resourceTemp.group === resource.group
                  );
                }
              );

              if (resourceSettingSubGroup) {
                if (keys.length === 0 || !keys.includes(resource.subGroup)) {
                  if (resource?.name !== AccessRuleEnum.COMPANY) {
                    resourceSettingSubGroup.features = [resource];
                  }
                  resourceSettings[resource.subGroup] = resourceSettingSubGroup;
                } else if (
                  keys.includes(resource.subGroup) &&
                  resource.name !== resource.subGroup
                ) {
                  resourceSettings[resource.subGroup]?.features?.push(resource);
                }
              }
            }
          });

          resourceGroups[ResourceGroupNameEnum.SETTINGS] = [];
          Object?.keys(resourceSettings)?.forEach((groupName: string) => {
            resourceGroups[ResourceGroupNameEnum.SETTINGS]?.push(resourceSettings[groupName]);
          });

          const orderedResourceGroups = handleOrderResources(resourceGroups);
          setResourceGroupsDictionary(orderedResourceGroups);
          const expandedResources = Object?.keys(orderedResourceGroups)?.map((groupName) => ({
            name: groupName,
            expanded: false,
          }));
          setResourcesExpand(expandedResources);
        }
      },
    }
  );

  const { isFetching: isFetchingGroupsInAccessRule } = useQuery(
    ["groups.list"],
    () =>
      GroupService.getGroups({
        companyId: currentUser?.companyId,
        roleId: accessRule?.id,
      }),
    {
      enabled: !!currentUser?.id && !!accessRule?.id && viewGroupsAssignedToAccessRule,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setGroupsInAccessRule(response.data);
      },
      onError: (error: AxiosError<Error>) => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"));
      },
    }
  );

  useQuery(
    ["permissions.get-by-role"],
    () => RoleService.getPermissionsByRoleId(accessRule?.id ?? 0),
    {
      enabled: isOpenAccessRuleDrawer && !!currentUser?.id && !!accessRule?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response?.data) {
        }
      },
    }
  );

  const closeAccessRuleInfoViewOnly = () => {
    setIsOpenAccessRuleInfoViewOnly(false);
  };

  const schema = yup.object().shape({
    name: yup
      .string()
      .matches(
        regexSpecialCharacter,
        translate("pleaseUseOnlyLettersNumbersAndSpacesSpecialCharactersAreNotAllowed")
      )
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    status: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
  });

  const isValidForm = (form: any, fieldName?: string) => {
    try {
      schema.validateSync(
        {
          name: form.name,
          status: form.status,
        },
        { abortEarly: false }
      );
      setErrors([]);
      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        if (fieldName) {
          const errorForField = error?.inner?.find((err) => err?.path === fieldName);
          setErrors([errorForField as any]);
        } else {
          setErrors(error?.inner);
        }
      }

      return false;
    }
  };

  const handleFormChange = (newForm: any, fieldName: string): void => {
    setAccessRuleForm(newForm);
    setIsDisabledSubmit(!isValidForm(newForm, fieldName));
  };

  const handleCleanFields = () => {
    setAccessRuleForm({
      name: "",
      description: "",
      status: Status.ACTIVE,
    });
    setAccessRule({} as IRoles);
    setIsDisabledForm(false);
    setErrors([]);
    setIsDisabledSubmit(true);
    setHasFormBeenSubmitted(false);
    setResourceGroupsDictionary({});
    setRoleResources([]);
    setResourcesExpand([]);
    setGroupsInAccessRule([]);
    setFunctionPermissions([]);
    setMasterDataPermissions([]);
    setErrorSpecialCharacter(false);
    setErrorUniqueAccessRuleName(false);
    setViewGroupsAssignedToAccessRule(false);
  };

  const open = useCallback((accessRule: IRoles) => {
    setAccessRule(accessRule);
    setIsOpenAccessRuleDrawer(true);
  }, []);

  const close = useCallback(() => {
    setIsOpenAccessRuleDrawer(false);
    handleCleanFields();
  }, []);

  const fieldHasError = (field: string) => !!errors?.find((error) => error?.path === field);
  const getErrorByField = (field: string) =>
    errors?.find((error) => error?.path === field)?.message;

  const handleSubmit = () => {
    let payload: any = {};
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);
    if (!isValidForm(accessRuleForm)) {
      return;
    }

    setErrorSpecialCharacter(false);
    setErrorUniqueAccessRuleName(false);
    const permissions = functionPermissions
      .filter((functionObj) => functionObj?.checked)
      ?.map((func) => {
        return {
          id: func?.id,
          resourceId: func?.resourceId,
          name: func?.name,
          code: func?.code,
          objectType: func?.objectType,
          objectData: func?.objectData ?? "",
        };
      });
    const dataPermissions = masterDataPermissions
      .filter((functionObj) => functionObj?.checked)
      ?.map((func) => {
        return {
          id: func?.id,
          resourceId: func?.resourceId,
          name: func?.name,
          code: func?.code,
          objectType: func?.objectType,
          objectData: func?.objectData ?? "",
        };
      });
    setPermissions([...permissions, ...dataPermissions]);
    functionPermissions?.forEach((permission) => {
      if (
        (permission?.code === "FUNCTION_SETTINGS_PEOPLE_MEMBERS_VIEW" ||
          permission?.code === "FUNCTION_SETTINGS_PEOPLE_MEMBERS_EDIT" ||
          permission?.code === "FUNCTION_SETTINGS_PEOPLE_MEMBERS_ADD_NEW") &&
        !roleResources?.some((resource) => resource?.id === memberResource?.id)
      ) {
        roleResources?.push({
          id: memberResource?.id ?? 0,
          view: true,
          edit: true,
          add: false,
          delete: false,
        });
      }

      if (
        (permission?.code === "FUNCTION_RESOURCES_SUB_CONTRACTORS_VIEW" ||
          permission?.code === "FUNCTION_RESOURCES_SUB_CONTRACTORS_EDIT" ||
          permission?.code === "FUNCTION_RESOURCES_SUB_CONTRACTORS_ADD_NEW") &&
        !roleResources?.some((resource) => resource?.id === subContractorResource?.id)
      ) {
        roleResources?.push({
          id: subContractorResource?.id ?? 0,
          view: true,
          edit: true,
          add: false,
          delete: false,
        });
      }

      if (
        (permission?.code === "FUNCTION_RESOURCES_EQUIPMENTS_VIEW" ||
          permission?.code === "FUNCTION_RESOURCES_EQUIPMENTS_EDIT" ||
          permission?.code === "FUNCTION_RESOURCES_EQUIPMENTS_ADD_NEW") &&
        !roleResources?.some((resource) => resource?.id === equipmentResource?.id)
      ) {
        roleResources?.push({
          id: equipmentResource?.id ?? 0,
          view: true,
          edit: true,
          add: false,
          delete: false,
        });
      }
    });

    payload = {
      ...accessRuleForm,
      companyId: currentUser?.companyId,
      type: GroupType.CUSTOM,
      status: accessRuleForm?.status,
      resources: [...roleResources],
    };

    if (accessRule?.id) {
      updateAccessRule({
        id: accessRule?.id,
        role: { ...payload, id: accessRule?.id },
      });
    } else {
      createRole(payload);
    }
  };

  const handleDuplicate = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);
    duplicateRole(accessRule?.id ?? 0);
  };

  const handleDelete = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);
    deleteAccessRule({ id: accessRule?.id ?? 0, role: accessRule });
  };

  useEffect(() => {
    if (errorSpecialCharacter) {
      setErrors([
        new yup.ValidationError(
          translate("pleaseUseOnlyLettersNumbersAndSpacesSpecialCharactersAreNotAllowed", {
            accessRuleName: accessRuleForm.name,
          }),
          accessRuleForm.name,
          "name"
        ),
      ]);
      setIsDisabledSubmit(true);
    }

    if (errorUniqueAccessRuleName) {
      setErrors([
        new yup.ValidationError(
          translate("theAccessRuleNameAlreadyExistsPleaseChooseADifferentName", {
            accessRuleName: accessRuleForm.name,
          }),
          accessRuleForm.name,
          "name"
        ),
      ]);
      setIsDisabledSubmit(true);
    }

    if (
      hasFormBeenSubmitted &&
      !isCreating &&
      !isDuplicating &&
      !isLoadingUpdatingAccessRule &&
      !isDeletingAccessRule &&
      !errorSpecialCharacter &&
      !errorUniqueAccessRuleName &&
      isOpenAccessRuleDrawer &&
      !isMutatingAccessRule
    ) {
      close();
    }
  }, [
    errorSpecialCharacter,
    errorUniqueAccessRuleName,
    hasFormBeenSubmitted,
    isOpenAccessRuleDrawer,
    isCreating,
    isDuplicating,
    isLoadingUpdatingAccessRule,
    isDeletingAccessRule,
    isMutatingAccessRule,
  ]);

  useEffect(() => {
    setIsDisabledForm(isMutatingAccessRule);
    setIsShowLoading(isMutatingAccessRule);
  }, [isMutatingAccessRule]);

  useEffect(() => {
    if (accessRule?.id) {
      setAccessRuleForm({
        name: accessRule?.name ?? "",
        description: accessRule?.description ?? "",
        status: accessRule?.status,
      });

      setIsDisabledForm(accessRule?.type === GroupType.SYSTEM);
      setIsDisabledSubmit(false);
    }
    setIsOpenAccessRuleInfoViewOnly(accessRule?.type === GroupType.SYSTEM);
  }, [accessRule]);

  const handleChangeResourceCheckbox = (
    id: number,
    attribute: "add" | "edit" | "view" | "delete",
    value: boolean,
    resource: Resources
  ) => {
    const arrRoleResource = roleResources;

    if (arrRoleResource.length === 0)
      arrRoleResource.push({
        id,
        view: attribute === "view" ? value : false,
        edit: attribute === "edit" ? value : false,
        add: attribute === "add" ? value : false,
        delete: attribute === "delete" ? value : false,
      });
    else {
      const findRoleResource = arrRoleResource
        .map((resource, index: number) => (resource.id === id ? { data: resource, index } : false))
        .filter((item) => typeof item !== "boolean")[0];

      if (findRoleResource && typeof findRoleResource !== "boolean") {
        const { data, index } = findRoleResource;

        data[attribute] = value;
        arrRoleResource[index] = data;
      } else {
        arrRoleResource.push({
          id,
          view: attribute === "view" ? value : false,
          edit: attribute === "edit" ? value : false,
          add: attribute === "add" ? value : false,
          delete: attribute === "delete" ? value : false,
        });
      }
    }

    setRoleResources([...arrRoleResource]);
    handleClearPermissionBaseOnResourceChange(resource);
  };

  const handleSetRoleResourceCheckbox = (
    id: number,
    attribute: "add" | "edit" | "view" | "delete"
  ): boolean => {
    const findRoleResource = roleResources.find((resource) => resource.id === id);

    if (findRoleResource) return findRoleResource[attribute];

    return false;
  };

  const handleClearPermissionBaseOnResourceChange = (resource: Resources) => {
    const findRoleResource = roleResources?.find(
      (roleResource) => roleResource?.id === resource?.id
    );
    if (findRoleResource) {
      let newFunctionPermissions = functionPermissions;
      let newMasterDataPermissions = masterDataPermissions;
      if (!findRoleResource?.view) {
        let viewFeatureFunctions: ResourceFunction[] = [];
        if (RESOURCE_HAVE_VIEW_FEATURE?.some((featureName) => resource?.name === featureName)) {
          viewFeatureFunctions =
            resource?.features?.find((feature) => feature?.name?.toLowerCase()?.includes("view"))
              ?.functions ?? [];
          newFunctionPermissions = newFunctionPermissions?.filter(
            (permission) =>
              !viewFeatureFunctions?.some(
                (featureFunction) => featureFunction?.id === permission?.id
              )
          );
        }

        if (RESOURCE_HAVE_MASTER_DATA?.some((dataName) => resource?.name === dataName)) {
          const masterDataFunctionsList = resource?.features?.filter((feature) => {
            return (
              feature?.name?.toLowerCase()?.includes("view") ||
              feature?.functions?.some(
                (featureFunction) =>
                  featureFunction?.objectData && featureFunction?.objectData !== ""
              )
            );
          });
          masterDataFunctionsList?.forEach((masterDataFunctions) => {
            newMasterDataPermissions = newMasterDataPermissions?.filter(
              (permission) =>
                !masterDataFunctions?.functions?.some(
                  (featureFunction) => featureFunction?.id === permission?.id
                )
            );
          });
          setMasterDataPermissions(newMasterDataPermissions);
        }

        newFunctionPermissions = newFunctionPermissions?.filter(
          (permission) =>
            (!permission?.name?.toLowerCase()?.includes("view") &&
              permission?.resourceId === findRoleResource?.id) ||
            permission?.resourceId !== findRoleResource?.id
        );
        setFunctionPermissions(newFunctionPermissions);
      }

      if (!findRoleResource?.edit) {
        let viewFeatureFunctions: ResourceFunction[] = [];
        if (RESOURCE_HAVE_VIEW_FEATURE.some((featureName) => resource?.name === featureName)) {
          viewFeatureFunctions =
            resource?.features?.find((feature) => feature?.name?.toLowerCase()?.includes("view"))
              ?.functions ?? [];
        }

        newFunctionPermissions = newFunctionPermissions?.filter(
          (permission) =>
            (permission?.resourceId === findRoleResource?.id &&
              permission?.name?.toLowerCase()?.includes("view")) ||
            permission?.resourceId !== findRoleResource?.id ||
            viewFeatureFunctions?.some((featureFunction) => featureFunction?.id === permission?.id)
        );
        setFunctionPermissions(newFunctionPermissions);
      }
    }
  };

  const handleExpandPermission = (expanded: boolean, groupName: string) => {
    setResourcesExpand((prev) =>
      prev?.map((resource) =>
        resource.name === groupName || resource.name === ResourceGroupNameEnum.GENERAL
          ? { ...resource, expanded }
          : resource
      )
    );
  };

  const handleToggleAllPermission = () => {
    const isSomePermissionExpand = resourcesExpand?.some((resource) => resource?.expanded === true);
    setResourcesExpand((prev) =>
      prev?.map((resource) => ({ ...resource, expanded: !isSomePermissionExpand }))
    );
  };

  const handleToggleViewGroupAssignedToAccessRule = () => {
    setViewGroupsAssignedToAccessRule((prev) => !prev);
  };

  const handleCheckFullAccessPermissions = (
    resource: Resources,
    isNestedFeatures: boolean = false,
    isHaveDataPermission: boolean = false
  ) => {
    if (isHaveDataPermission) {
      let fullFunctionPermissions: ResourceFunction[] = [];
      let dataPermissions: ResourceFunction[] = [];
      resource?.features?.forEach((featureObj) => {
        return featureObj?.functions?.forEach((functionObj) => {
          if (functionObj?.objectData && functionObj?.objectData !== "") {
            dataPermissions?.push(functionObj);
          } else {
            fullFunctionPermissions?.push(functionObj);
          }
        });
      });

      const isFullFunctionAccess = fullFunctionPermissions?.every((functionObj) => {
        return functionPermissions?.some((obj) => obj?.code === functionObj?.code);
      });
      const isFullDataAccess = dataPermissions?.every((functionObj) => {
        return masterDataPermissions?.some(
          (obj) => obj?.id === functionObj?.id && obj?.resourceId === functionObj?.resourceId
        );
      });

      return isFullDataAccess && isFullFunctionAccess;
    }

    if (isNestedFeatures) {
      return resource?.features?.every((resourceFeature) => {
        return resourceFeature?.features?.every((featureObj) => {
          return featureObj?.functions?.every((functionObj) => {
            return functionPermissions?.some((obj) => obj?.code === functionObj?.code);
          });
        });
      });
    }

    return resource?.features?.every((featureObj) => {
      return featureObj?.functions?.every((functionObj) => {
        return functionPermissions?.some((obj) => obj?.code === functionObj?.code);
      });
    });
  };

  const handleCheckPartialAccessPermissions = (
    resource: Resources,
    isNestedFeatures: boolean = false,
    isHaveDataPermission: boolean = false
  ) => {
    if (isHaveDataPermission) {
      let partialFunctionPermissions: ResourceFunction[] = [];
      let dataPermissions: ResourceFunction[] = [];
      resource?.features?.forEach((featureObj) => {
        return featureObj?.functions?.forEach((functionObj) => {
          if (functionObj?.objectData && functionObj?.objectData !== "") {
            dataPermissions?.push(functionObj);
          } else {
            partialFunctionPermissions?.push(functionObj);
          }
        });
      });

      const isPartialFunctionAccess = partialFunctionPermissions?.some((functionObj) => {
        return functionPermissions?.some((obj) => obj?.code === functionObj?.code);
      });
      const isPartialDataAccess = dataPermissions?.some((functionObj) => {
        return masterDataPermissions?.some(
          (obj) => obj?.id === functionObj?.id && obj?.resourceId === functionObj?.resourceId
        );
      });

      return isPartialDataAccess || isPartialFunctionAccess;
    }

    if (isNestedFeatures) {
      return resource?.features?.some((resourceFeature) => {
        return resourceFeature?.features?.some((featureObj) => {
          return featureObj?.functions?.some((functionObj) => {
            return functionPermissions?.some((obj) => obj?.code === functionObj?.code);
          });
        });
      });
    }

    return resource?.features?.some((featureObj) => {
      return featureObj?.functions?.some((functionObj) => {
        return functionPermissions?.some((obj) => obj?.code === functionObj?.code);
      });
    });
  };

  const handleSettingResourcePermission = (groupName: string, markTrue: boolean = false) => {
    if (groupName !== ResourceGroupNameEnum.SETTINGS) return;

    let arrRoleResource = roleResources;
    if (markTrue) {
      const setting = roleResources?.find((role) => role?.id === settingResource?.id);
      if (!setting) {
        arrRoleResource.push({
          id: settingResource?.id ?? 0,
          view: true,
          edit: true,
          add: false,
          delete: false,
        });
        setRoleResources([...arrRoleResource]);
      }
    } else {
      const setting = roleResources?.find((role) => role?.id === settingResource?.id);
      if (setting) {
        arrRoleResource = roleResources?.filter((role) => role?.id !== settingResource?.id);
        setRoleResources([...arrRoleResource]);
      }
    }
  };

  const handleNetworkResourcePermission = (groupName: string, markTrue: boolean = false) => {
    if (groupName !== ResourceGroupNameEnum.NETWORK) return;

    let arrRoleResource = roleResources;
    if (markTrue) {
      const network = roleResources?.find((role) => role?.id === networkResource?.id);
      if (!network) {
        arrRoleResource.push({
          id: networkResource?.id ?? 0,
          view: true,
          edit: true,
          add: false,
          delete: false,
        });
        setRoleResources([...arrRoleResource]);
      }
    } else {
      const network = roleResources?.find((role) => role?.id === networkResource?.id);
      if (network) {
        arrRoleResource = roleResources?.filter((role) => role?.id !== networkResource?.id);
        setRoleResources([...arrRoleResource]);
      }
    }
  };

  const handleReportResourcePermission = (groupName: string, markTrue: boolean = false) => {
    if (
      groupName !== ResourceGroupNameEnum.ANALYTICS &&
      groupName !== ResourceGroupNameEnum.REPORTS
    )
      return;

    let arrRoleResource = roleResources;
    if (markTrue) {
      const report = roleResources?.find((role) => role?.id === reportResource?.id);
      if (!report) {
        arrRoleResource.push({
          id: reportResource?.id ?? 0,
          view: true,
          edit: true,
          add: false,
          delete: false,
        });
        setRoleResources([...arrRoleResource]);
      }
    } else {
      const report = roleResources?.find((role) => role?.id === reportResource?.id);
      if (report) {
        arrRoleResource = roleResources?.filter((role) => role?.id !== reportResource?.id);
        setRoleResources([...arrRoleResource]);
      }
    }
  };

  const renderStatusBadge = (groupName: string) => {
    const resource = resourceGroupDictionary[groupName];
    if (resource || (roleResources && roleResources?.length > 0)) {
      let resourceHaveDataPermission = false;
      const fullAccessDataPermission = resource?.every((resourceObj) => {
        resourceHaveDataPermission =
          resourceObj?.name === ResourceGroupNameEnum.ANALYTICS ||
          resourceObj?.name === ResourceName.MOVEMENT ||
          resourceObj?.name === ResourceName.TRANSSHIPMENT ||
          resourceObj?.name === ResourceName.STORAGE_BOX_AND_UNIT;
        return handleCheckFullAccessPermissions(resourceObj, false, resourceHaveDataPermission);
      });

      const fullAccessPermission = resource?.every((resourceObj) => {
        return handleCheckFullAccessPermissions(
          resourceObj,
          groupName === ResourceGroupNameEnum.SETTINGS &&
            resourceObj?.name !== AccessRuleEnum.COMPANY
        );
      });

      const fullAccessResource = resource?.every((permission) => {
        const isHavePermission = roleResources?.find(
          (roleResource) => roleResource?.id === permission?.id
        );

        if (!isHavePermission) return false;

        if (
          groupName === getResourceGroupName(ResourceGroupNameEnum.OVERVIEW) ||
          groupName === getResourceGroupName(ResourceGroupNameEnum.STATS)
        ) {
          return isHavePermission?.view;
        }

        return isHavePermission?.view && isHavePermission?.edit;
      });

      if (
        (fullAccessResource && fullAccessPermission) ||
        (resourceHaveDataPermission && fullAccessResource && fullAccessDataPermission)
      ) {
        handleSettingResourcePermission(groupName, true);
        handleNetworkResourcePermission(groupName, true);
        if (isSupplyChain(currentUser?.company)) {
          handleReportResourcePermission(groupName, true);
        }
        return <Badge className={classes.statusBadgeFullAccess}>{translate("fullAccess")}</Badge>;
      }

      const partialAccessDataPermission = resource?.some((resourceObj) => {
        resourceHaveDataPermission =
          resourceObj?.name === ResourceGroupNameEnum.ANALYTICS ||
          resourceObj?.name === ResourceName.MOVEMENT ||
          resourceObj?.name === ResourceName.TRANSSHIPMENT ||
          resourceObj?.name === ResourceName.STORAGE_BOX_AND_UNIT;
        return handleCheckPartialAccessPermissions(resourceObj, false, resourceHaveDataPermission);
      });

      const partialAccessPermission = resource?.some((resourceObj) => {
        return handleCheckPartialAccessPermissions(
          resourceObj,
          groupName === ResourceGroupNameEnum.SETTINGS &&
            resourceObj?.name !== AccessRuleEnum.COMPANY
        );
      });

      const partialAccessResource = resource?.some((permission) => {
        const isHavePermission = roleResources?.find(
          (roleResource) => roleResource?.id === permission?.id
        );

        if (!isHavePermission) return false;

        if (
          groupName === getResourceGroupName(ResourceGroupNameEnum.OVERVIEW) ||
          groupName === getResourceGroupName(ResourceGroupNameEnum.STATS)
        ) {
          return isHavePermission?.view;
        }

        return isHavePermission?.view || isHavePermission?.edit;
      });

      if (
        partialAccessResource ||
        partialAccessPermission ||
        (resourceHaveDataPermission && (partialAccessResource || partialAccessDataPermission))
      ) {
        handleSettingResourcePermission(groupName, true);
        handleNetworkResourcePermission(groupName, true);
        if (isSupplyChain(currentUser?.company)) {
          handleReportResourcePermission(groupName, true);
        }
        return (
          <Badge className={classes.statusBadgePartialAccess}>{translate("partialAccess")}</Badge>
        );
      }

      handleSettingResourcePermission(groupName, false);
      handleNetworkResourcePermission(groupName, false);
      if (isSupplyChain(currentUser?.company)) {
        handleReportResourcePermission(groupName, false);
      }
      return <Badge className={classes.statusBadge}>{translate("noAccess")}</Badge>;
    }

    return <Badge className={classes.statusBadge}>{translate("noAccess")}</Badge>;
  };

  const badgeStatus = (status: number) => (
    <Badge
      className={`
          ${classes.statusBadge}
          ${
            statusConverter(status) === Status.ACTIVE
              ? classes.statusBadgeActive
              : classes.statusBadgeInactive
          }
      `}
    >
      {statusConverter(status)}
    </Badge>
  );

  const groupColumns: Column[] = [
    {
      name: "name",
      title: translate("group"),
    },
    {
      name: "type",
      title: translate("type"),
      formatText: (type) => (type === GroupType.SYSTEM ? `${GroupType.SYSTEM} default` : type),
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (status: number) => badgeStatus(status),
      hideSearch: true,
    },
  ];

  const renderPermissionsOrGroupAssignedSection = () => {
    if (viewGroupsAssignedToAccessRule) {
      return (
        <Grid style={{ marginTop: "24px" }} item xs={12} sm={12} md={12} lg={12}>
          <SkeletonComponent isLoading={isFetchingGroupsInAccessRule}>
            <div className={classes.gridWrapper}>
              <ShipeeziNewGrid
                data={groupsInAccessRule ?? []}
                columns={groupColumns}
                keyExtractor={(data) => data.id ?? ""}
                pageSize={20}
                currentPage={page}
                onNextPage={setCurrentPage}
                onPreviousPage={setCurrentPage}
                onGoToPage={setCurrentPage}
                showSelection={false}
              />
            </div>
          </SkeletonComponent>
        </Grid>
      );
    }

    return (
      <Grid style={{ marginTop: "24px" }} item xs={12} sm={12} md={12} lg={12}>
        <Card className={classes.resourceWrapper}>
          <div className={classes.resourceHeader}>
            <p className={classes.resourceTitle}>{translate("permissions")}</p>
            <p onClick={() => handleToggleAllPermission()} className={classes.resourceExpand}>
              {resourcesExpand?.some((resource) => resource?.expanded === true)
                ? translate("collapseAll")
                : translate("expandAll")}
            </p>
          </div>
          <CardContent className={classes.resourceContent}>
            {Object?.keys(resourceGroupDictionary)?.map((groupName) => {
              return (
                <Accordion
                  key={groupName}
                  disableGutters
                  className={classes.accordion}
                  expanded={
                    resourcesExpand?.find((resource) => resource?.name === groupName)?.expanded ||
                    false
                  }
                  onChange={(e: React.SyntheticEvent, expanded: boolean) =>
                    handleExpandPermission(expanded, groupName)
                  }
                >
                  <AccordionSummary
                    className={classes.accordionSummary}
                    style={{
                      backgroundColor: resourcesExpand?.find(
                        (resource) => resource?.name === groupName
                      )?.expanded
                        ? "#F9FAFB"
                        : "transparent",
                    }}
                    aria-controls="panel1-content"
                    id="panel1-header"
                  >
                    <IconTypo className={classes.accordionIconContainer}>
                      <img
                        src={resourceIcons[groupName]}
                        alt=""
                        width={20}
                        height={20}
                        className={classes.iconResource}
                      />
                      <InfoDetails>
                        <Typography className={classes.accordionSummaryTitle} component="span">
                          {translate(groupName)}
                        </Typography>
                      </InfoDetails>
                    </IconTypo>
                    <div className={classes.accordionSummaryAction}>
                      {renderStatusBadge(groupName)}
                      <ExpandMoreIcon
                        style={{
                          marginLeft: "12px",
                          transform: resourcesExpand?.find(
                            (resource) => resource?.name === groupName
                          )?.expanded
                            ? "rotate(180deg)"
                            : "none",
                        }}
                      />
                    </div>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box display="flex" flexDirection="column" alignItems="flex-end">
                      {resourceGroupDictionary[groupName]?.map((resource, index) => {
                        const isViewOnlyGroup =
                          groupName === getResourceGroupName(ResourceGroupNameEnum.OVERVIEW) ||
                          groupName === getResourceGroupName(ResourceGroupNameEnum.STATS);
                        return (
                          <Card key={index} className={classes.accordionDetailsCard}>
                            <Grid
                              className={classes.accordionDetails}
                              container
                              direction="row"
                              alignItems="center"
                              key={index}
                            >
                              <Grid item xs={6} sm={6} md={8} lg={8}>
                                <span
                                  className={`
                                    ${classes.accordionDetailsTitle}
                                    ${classes.accordionDetailsLabel}
                                  `}
                                >
                                  {getResourceGroupName(resource.name) === groupName
                                    ? translate("general")
                                    : getResourceGroupName(resource.name)}
                                </span>
                              </Grid>
                              <Grid item container xs={6} sm={6} md={4} lg={4} spacing={2}>
                                {isViewOnlyGroup && <Grid item xs={6} sm={6} md={6} lg={6}></Grid>}
                                <Grid item xs={6} sm={6} md={6} lg={6}>
                                  <Checkbox
                                    className={classes.accordionResourceCheckbox}
                                    disabled={accessRule?.type === RoleType.SYSTEM}
                                    control={{
                                      "data-cy": resource.name,
                                    }}
                                    label={"View"}
                                    checked={handleSetRoleResourceCheckbox(
                                      resource?.id || 0,
                                      "view"
                                    )}
                                    onChange={(_, checked) =>
                                      handleChangeResourceCheckbox(
                                        resource?.id || 0,
                                        "view",
                                        checked,
                                        resource
                                      )
                                    }
                                  />
                                </Grid>
                                {!isViewOnlyGroup && (
                                  <Grid item xs={6} sm={6} md={6} lg={6}>
                                    <Checkbox
                                      className={classes.accordionResourceCheckbox}
                                      disabled={accessRule?.type === RoleType.SYSTEM}
                                      control={{
                                        "data-cy": resource.name,
                                      }}
                                      label="Edit"
                                      checked={handleSetRoleResourceCheckbox(
                                        resource?.id || 0,
                                        "edit"
                                      )}
                                      onChange={(_, checked) =>
                                        handleChangeResourceCheckbox(
                                          resource?.id || 0,
                                          "edit",
                                          checked,
                                          resource
                                        )
                                      }
                                    />
                                  </Grid>
                                )}
                              </Grid>
                            </Grid>
                            {resource?.features && resource?.features?.length > 0 && (
                              <CardContent style={{ padding: "0px" }}>
                                <div className={classes.accordionDetailsCardContent}>
                                  {groupName ===
                                    getResourceGroupName(ResourceGroupNameEnum.SETTINGS) &&
                                  resource?.name !== AccessRuleEnum.COMPANY
                                    ? resource?.features?.map((resourceChild) => {
                                        return resourceChild?.features?.map(
                                          (feature, index: number) => {
                                            return (
                                              <AccessRuleFeatureFunctionCard
                                                key={index}
                                                feature={feature}
                                                functionPermissions={functionPermissions}
                                                masterDataPermissions={masterDataPermissions}
                                                resource={resource}
                                                roleResources={roleResources}
                                                setFunctionPermissions={setFunctionPermissions}
                                                setMasterDataPermissions={setMasterDataPermissions}
                                                accessRule={accessRule}
                                              />
                                            );
                                          }
                                        );
                                      })
                                    : resource?.features?.map((feature, index: number) => {
                                        return (
                                          <AccessRuleFeatureFunctionCard
                                            key={index}
                                            feature={feature}
                                            functionPermissions={functionPermissions}
                                            masterDataPermissions={masterDataPermissions}
                                            resource={resource}
                                            roleResources={roleResources}
                                            setFunctionPermissions={setFunctionPermissions}
                                            setMasterDataPermissions={setMasterDataPermissions}
                                            accessRule={accessRule}
                                          />
                                        );
                                      })}
                                </div>
                              </CardContent>
                            )}
                          </Card>
                        );
                      })}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const renderAccessRuleInfoForViewOnly = () => {
    if (!isOpenAccessRuleInfoViewOnly) return;

    return (
      <Grid style={{ marginBottom: "24px" }} item xs={12} sm={12} md={12} lg={12}>
        <div className={classes.groupInfo}>
          <InfoOutlined className={classes.groupInfoIcon} />
          <span className={classes.groupInfoContent}>
            {translate(
              "thisIsASystemDefaultAccessRuleAndCannotBeModifiedYouAreWelcomeToCreateANewAccessRuleOrDuplicateAsNeeded"
            )}
          </span>
          <Close
            style={{ cursor: "pointer" }}
            onClick={closeAccessRuleInfoViewOnly}
            className={classes.groupInfoIcon}
          />
        </div>
      </Grid>
    );
  };

  const renderAccessRuleForm = () => {
    return (
      <AccessRuleForm>
        <Grid container direction="row" alignItems="flex-start">
          {renderAccessRuleInfoForViewOnly()}
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Input
              required
              disabled={isDisabledForm}
              labelClassName={
                fieldHasError("name")
                  ? classes.inputLabel
                  : isDisabledForm
                  ? classes.labelDisabled
                  : classes.label
              }
              name="name"
              label={translate("accessRuleName")}
              error={fieldHasError("name")}
              errorMessage={getErrorByField("name")}
              placeholder={translate("enterNameForThisAccessRule")}
              value={accessRuleForm?.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFormChange({ ...accessRuleForm, name: e?.target?.value }, "name")
              }
            />
          </Grid>
          <Grid style={{ marginTop: "12px" }} item xs={12} sm={12} md={12} lg={12}>
            <Input
              disabled={isDisabledForm}
              name="description"
              labelClassName={isDisabledForm ? classes.labelDisabled : classes.label}
              label={translate("description")}
              error={fieldHasError("description")}
              errorMessage={getErrorByField("description")}
              placeholder={translate("describeThisAccessRuleInFewWords")}
              value={accessRuleForm?.description}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFormChange(
                  { ...accessRuleForm, description: e?.target?.value },
                  "description"
                )
              }
            />
          </Grid>
          <Grid style={{ marginTop: "12px" }} item xs={12} sm={12} md={12} lg={12}>
            <CustomTooltip
              title={
                !!accessRule?.id && accessRule?.qty > 0
                  ? translate("thisRuleIsCurrentlyUtilizedByGroupsAndCannotBeDeactivated")
                  : ""
              }
            >
              <div>
                <Select
                  disabled={isDisabledForm || (!!accessRule?.id && accessRule?.qty > 0)}
                  required
                  labelStyle={{
                    fontFamily: "Manrope",
                    fontWeight: 600,
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "0.1px",
                    verticalAlign: "middle",
                    marginBottom: "4px",
                    color: fieldHasError("status")
                      ? "#D92D20"
                      : isDisabledForm || (!!accessRule?.id && accessRule?.qty > 0)
                      ? "#98A2B3"
                      : "#004D95",
                  }}
                  tabIndex={0}
                  name="status"
                  label={translate("status")}
                  error={fieldHasError("status")}
                  errorMessage={getErrorByField("status")}
                  value={accessRuleForm?.status}
                  onChange={(e: SelectChangeEvent<any>) =>
                    handleFormChange({ ...accessRuleForm, status: e?.target?.value }, "status")
                  }
                >
                  {accessRuleStatus?.map((status) => (
                    <MenuItem
                      className={classes.selectItem}
                      key={status.value}
                      value={status.value}
                    >
                      {status.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </CustomTooltip>
          </Grid>
          {accessRule?.qty && accessRule?.qty > 0 ? (
            <Grid style={{ marginTop: "24px" }} item xs={12} sm={12} md={12} lg={12}>
              <div
                onClick={() => handleToggleViewGroupAssignedToAccessRule()}
                className={classes.viewGroup}
              >
                <IconTypo>
                  <img
                    src={arrowRightIcon}
                    alt=""
                    width={16}
                    height={16}
                    className={classes.iconResource}
                    style={{
                      transform: viewGroupsAssignedToAccessRule ? "rotate(180deg)" : "none",
                    }}
                  />
                </IconTypo>
                <Typography className={classes.viewGroupTitle} component="span">
                  {viewGroupsAssignedToAccessRule
                    ? translate("returnToPermissions")
                    : translate("thisAccessRuleIsUtilizedBy", {
                        group: `${accessRule?.qty} ${accessRule?.qty > 1 ? "groups" : "group"}`,
                      })}
                </Typography>
              </div>
            </Grid>
          ) : (
            ""
          )}
          {renderPermissionsOrGroupAssignedSection()}
        </Grid>
      </AccessRuleForm>
    );
  };

  const renderAccessRuleFooter = () => {
    if (accessRule?.id && accessRule?.type === GroupType.SYSTEM) {
      return (
        <MainFooter>
          <CustomTooltip title="Duplicate">
            <div className={classes.duplicateBtnContainer}>
              <Button
                className={classes.duplicateBtn}
                disabled={isMutatingAccessRule}
                variant="outlined"
                onClick={handleDuplicate}
              >
                <IconTypo>
                  <img
                    src={duplicateIcon}
                    alt=""
                    width={24}
                    height={24}
                    className={classes.iconResource}
                  />
                </IconTypo>
              </Button>
            </div>
          </CustomTooltip>
          <MaiFooterSubmit>
            <Button
              variant="text"
              className={classes.btnClose}
              onClick={close}
              disabled={isMutatingAccessRule}
            >
              {translate("close")}
            </Button>
          </MaiFooterSubmit>
        </MainFooter>
      );
    }

    if (accessRule?.id && accessRule?.type === GroupType.CUSTOM) {
      return (
        <MainFooter>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
            <CustomTooltip title="Duplicate">
              <div className={classes.duplicateBtnContainer}>
                <Button
                  className={classes.duplicateBtn}
                  disabled={isMutatingAccessRule}
                  variant="outlined"
                  onClick={handleDuplicate}
                >
                  <IconTypo>
                    <img
                      src={duplicateIcon}
                      alt=""
                      width={24}
                      height={24}
                      className={classes.iconResource}
                    />
                  </IconTypo>
                </Button>
              </div>
            </CustomTooltip>
            <CustomTooltip
              title={
                !!accessRule?.id && accessRule?.qty > 0
                  ? translate("thisRuleIsCurrentlyUtilizedByGroupsAndCannotBeDeleted")
                  : ""
              }
            >
              <div>
                <Button
                  disabled={(!!accessRule?.id && accessRule?.qty > 0) || isMutatingAccessRule}
                  variant="outlined"
                  className={classes.deleteBtn}
                  onClick={handleDelete}
                >
                  {translate("delete")}
                </Button>
              </div>
            </CustomTooltip>
          </div>
          <div
            style={{ display: "flex", alignItems: "center", flex: 1, justifyContent: "flex-end" }}
          >
            <Button
              variant="text"
              className={classes.btnClose}
              onClick={close}
              disabled={isMutatingAccessRule}
            >
              {translate("cancel")}
            </Button>
            <Button
              className={classes.btnSubmit}
              disabled={isDisabledForm || isDisabledSubmit}
              variant="contained"
              style={{
                marginLeft: "14px",
                backgroundColor: isDisabledForm || isDisabledSubmit ? "" : "#0055A4",
              }}
              onClick={handleSubmit}
            >
              {translate("update")}
            </Button>
          </div>
        </MainFooter>
      );
    }

    return (
      <MainFooter>
        <Button
          variant="text"
          className={classes.btnClose}
          onClick={close}
          disabled={isMutatingAccessRule}
        >
          {translate("cancel")}
        </Button>
        <MaiFooterSubmit>
          <Button
            className={classes.btnSubmit}
            disabled={isDisabledForm || isDisabledSubmit}
            variant="contained"
            style={{
              backgroundColor: isDisabledForm || isDisabledSubmit ? "" : "#0055A4",
            }}
            onClick={handleSubmit}
          >
            {translate("addAccessRule")}
          </Button>
        </MaiFooterSubmit>
      </MainFooter>
    );
  };

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  return (
    <Drawer
      anchor="right"
      ModalProps={{
        keepMounted: false,
        disableAutoFocus: true,
        disableEnforceFocus: true,
        disablePortal: true,
      }}
      PaperProps={{
        style: {
          overflow: "hidden",
          backgroundColor: "transparent",
          color: "inherit",
          width: useMediaQuery("(max-width: 600px)") ? "100%" : "624px",
        },
      }}
      open={isOpenAccessRuleDrawer}
      onClose={close}
    >
      <Container fullHeight={true}>
        {isShowLoading && (
          <div className={classes.overlayLoading}>
            <CircularProgress color="inherit" />
          </div>
        )}
        <Main>
          <MainTitle>
            <MainTitleLabel>{translate("accessRule")}</MainTitleLabel>
            <Close className={classes.closeRuleDrawer} onClick={() => close()} />
          </MainTitle>

          <MainContent>{renderAccessRuleForm()}</MainContent>

          {renderAccessRuleFooter()}
        </Main>
      </Container>
    </Drawer>
  );
});
